import { useState, useEffect, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isWithinInterval, parseISO, isBefore, startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight, X, Lock, CalendarDays } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { getHostProperties, getPropertyCalendar, createAvailabilityBlock, deleteAvailabilityBlock } from '../../lib/api';

export default function HostCalendar() {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // Selection state for blocking dates
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  
  // Dialog states
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockNote, setBlockNote] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);

  // Load properties on mount
  useEffect(() => {
    loadProperties();
  }, []);

  // Load calendar when property or month changes
  useEffect(() => {
    if (selectedPropertyId) {
      loadCalendar();
    }
  }, [selectedPropertyId, currentMonth]);

  const loadProperties = async () => {
    try {
      const res = await getHostProperties({ limit: 50 });
      const props = res.data.items || [];
      setProperties(props);
      if (props.length > 0) {
        setSelectedPropertyId(props[0].id);
      }
    } catch (err) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendar = async () => {
    setCalendarLoading(true);
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const res = await getPropertyCalendar(selectedPropertyId, month, year);
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  // Get days for the calendar grid
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Pad start with previous month's days to align to week
    const startDay = start.getDay();
    const paddedStart = [];
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(start);
      d.setDate(d.getDate() - (i + 1));
      paddedStart.push(d);
    }
    
    // Pad end to complete the week
    const totalDays = paddedStart.length + days.length;
    const paddedEnd = [];
    const remaining = 7 - (totalDays % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(end);
        d.setDate(d.getDate() + i);
        paddedEnd.push(d);
      }
    }
    
    return [...paddedStart, ...days, ...paddedEnd];
  }, [currentMonth]);

  // Get events for a specific day
  const getEventsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return events.filter(event => {
      const start = parseISO(event.start_date);
      const end = parseISO(event.end_date);
      // For bookings, end_date is checkout (exclusive)
      // For blocks, end_date is inclusive
      if (event.type === 'booking') {
        return dayStr >= event.start_date && dayStr < event.end_date;
      }
      return dayStr >= event.start_date && dayStr <= event.end_date;
    });
  };

  // Check if day is in selection range
  const isInSelection = (day) => {
    if (!selectionStart || !selectionEnd) return false;
    const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
    const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
    return isWithinInterval(day, { start, end });
  };

  // Handle day click for selection
  const handleDayClick = (day) => {
    const today = startOfDay(new Date());
    if (isBefore(day, today)) return; // Can't block past dates
    
    const dayEvents = getEventsForDay(day);
    
    // If clicking on a host block, show delete dialog
    const hostBlock = dayEvents.find(e => e.type === 'host_block');
    if (hostBlock && !isSelecting) {
      setSelectedBlock(hostBlock);
      setDeleteDialogOpen(true);
      return;
    }
    
    // If there's a booking, can't select
    if (dayEvents.some(e => e.type === 'booking')) {
      toast.error('Cannot block dates with existing bookings');
      return;
    }
    
    if (!isSelecting) {
      setSelectionStart(day);
      setSelectionEnd(day);
      setIsSelecting(true);
    } else {
      setSelectionEnd(day);
      setIsSelecting(false);
      setBlockDialogOpen(true);
    }
  };

  // Handle creating a block
  const handleCreateBlock = async () => {
    if (!selectionStart || !selectionEnd) return;
    
    const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
    const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
    
    try {
      await createAvailabilityBlock(selectedPropertyId, {
        property_id: selectedPropertyId,
        start_date: format(start, 'yyyy-MM-dd'),
        end_date: format(end, 'yyyy-MM-dd'),
        reason: 'host_block',
        note: blockNote || null
      });
      toast.success('Dates blocked successfully');
      setBlockDialogOpen(false);
      setBlockNote('');
      setSelectionStart(null);
      setSelectionEnd(null);
      loadCalendar();
    } catch (err) {
      toast.error('Failed to block dates');
    }
  };

  // Handle deleting a block
  const handleDeleteBlock = async () => {
    if (!selectedBlock) return;
    
    try {
      await deleteAvailabilityBlock(selectedPropertyId, selectedBlock.id);
      toast.success('Block removed successfully');
      setDeleteDialogOpen(false);
      setSelectedBlock(null);
      loadCalendar();
    } catch (err) {
      toast.error('Failed to remove block');
    }
  };

  // Cancel selection
  const cancelSelection = () => {
    setSelectionStart(null);
    setSelectionEnd(null);
    setIsSelecting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-12">
        <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-slate-900 mb-2">No Properties</h2>
        <p className="text-slate-500">Create a property first to manage its calendar.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="host-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Availability Calendar</h1>
          <p className="text-slate-500 mt-1">Manage your property availability</p>
        </div>
        
        {/* Property Selector */}
        <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
          <SelectTrigger className="w-full sm:w-64" data-testid="property-selector">
            <SelectValue placeholder="Select property" />
          </SelectTrigger>
          <SelectContent>
            {properties.map(prop => (
              <SelectItem key={prop.id} value={prop.id}>
                {prop.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span>Confirmed Booking</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-500 rounded"></div>
          <span>Pending Booking</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-400 rounded"></div>
          <span>Blocked by You</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-primary/30 rounded border-2 border-primary"></div>
          <span>Selected</span>
        </div>
      </div>

      {/* Selection Helper */}
      {isSelecting && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-blue-800">
            Click another date to complete your selection, or{' '}
            <button onClick={cancelSelection} className="underline">cancel</button>
          </span>
        </div>
      )}

      {/* Calendar Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <CardTitle className="text-lg">
              {format(currentMonth, 'MMMM yyyy')}
            </CardTitle>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {calendarLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map((day, idx) => {
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, new Date());
                const isPast = isBefore(day, startOfDay(new Date()));
                const dayEvents = getEventsForDay(day);
                const booking = dayEvents.find(e => e.type === 'booking');
                const block = dayEvents.find(e => e.type === 'host_block');
                const inSelection = isInSelection(day);
                
                let bgClass = 'bg-white';
                let textClass = isCurrentMonth ? 'text-slate-900' : 'text-slate-300';
                
                if (isPast) {
                  bgClass = 'bg-slate-50';
                  textClass = 'text-slate-300';
                } else if (booking) {
                  if (booking.status === 'paid' || booking.status === 'approved' || booking.status === 'completed') {
                    bgClass = 'bg-green-500 text-white';
                    textClass = 'text-white';
                  } else if (booking.status === 'pending') {
                    bgClass = 'bg-yellow-500 text-white';
                    textClass = 'text-white';
                  }
                } else if (block) {
                  bgClass = 'bg-slate-400 text-white';
                  textClass = 'text-white';
                } else if (inSelection) {
                  bgClass = 'bg-primary/30 border-2 border-primary';
                }
                
                return (
                  <div
                    key={idx}
                    onClick={() => isCurrentMonth && !isPast && handleDayClick(day)}
                    className={`
                      relative min-h-[60px] p-1 border border-slate-100 rounded
                      ${bgClass}
                      ${isCurrentMonth && !isPast ? 'cursor-pointer hover:border-primary' : 'cursor-default'}
                      ${isToday ? 'ring-2 ring-primary ring-offset-1' : ''}
                    `}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <span className={`text-xs font-medium ${textClass}`}>
                      {format(day, 'd')}
                    </span>
                    
                    {/* Event indicator */}
                    {booking && isCurrentMonth && (
                      <div className="text-[10px] truncate mt-0.5">
                        {booking.guest_display}
                      </div>
                    )}
                    {block && isCurrentMonth && !booking && (
                      <Lock className="w-3 h-3 mt-0.5" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Block Dates Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={(open) => {
        setBlockDialogOpen(open);
        if (!open) cancelSelection();
      }}>
        <DialogContent data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle>Block Dates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Block dates from{' '}
              <strong>{selectionStart && format(selectionStart < selectionEnd ? selectionStart : selectionEnd, 'MMM d, yyyy')}</strong>
              {' '}to{' '}
              <strong>{selectionEnd && format(selectionStart < selectionEnd ? selectionEnd : selectionStart, 'MMM d, yyyy')}</strong>
            </p>
            <div>
              <Label htmlFor="block-note">Note (optional)</Label>
              <Input
                id="block-note"
                placeholder="e.g., Personal use, Maintenance"
                value={blockNote}
                onChange={(e) => setBlockNote(e.target.value)}
                data-testid="block-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setBlockDialogOpen(false);
              cancelSelection();
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateBlock} data-testid="confirm-block-btn">
              Block Dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Block Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-block-dialog">
          <DialogHeader>
            <DialogTitle>Remove Block</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              Remove block from{' '}
              <strong>{selectedBlock && format(parseISO(selectedBlock.start_date), 'MMM d, yyyy')}</strong>
              {' '}to{' '}
              <strong>{selectedBlock && format(parseISO(selectedBlock.end_date), 'MMM d, yyyy')}</strong>?
            </p>
            {selectedBlock?.note && (
              <p className="text-sm text-slate-500 mt-2">
                Note: {selectedBlock.note}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteBlock} data-testid="confirm-delete-block-btn">
              Remove Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
